import { Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../../../components/Button";
import { TextArea } from "../../../../../../../components/TextArea";
import { TooltipText } from "../../../PatientMessagesTable.style";
import {
  PlusButton,
  PlusMenu,
  PlusMenuDescription,
  PlusMenuItem,
  PlusMenuTitle,
  ReopenTextAreaCss,
  ResponseContainer,
  SendAndReopenButtonCss,
  SendButtonCss,
  TextAreaCss,
} from "../MessageThreadModal.styles";

type ThreadComposerProps = {
  draft: string;
  onDraftChange: (draft: string) => void;
  onSend: () => void;
  /** Switches the modal to the full "New message" composer, carrying the draft across. */
  onCustom: () => void;
  isSending: boolean;
  isClosed: boolean;
  /** Why the composer is unavailable, or undefined when it is. */
  disabledReason?: string;
  /** Another action on the thread is in flight (closing it): locked, with no tooltip since it is momentary. */
  isBusy?: boolean;
  /**
   * The Custom step reuses CreateMessageModal, which renders nothing when the patient has no
   * registered device — so offering the entry then would open an empty modal.
   */
  isCustomAvailable: boolean;
};

export function ThreadComposer({
  draft,
  onDraftChange,
  onSend,
  onCustom,
  isSending,
  isClosed,
  disabledReason,
  isBusy = false,
  isCustomAvailable,
}: ThreadComposerProps) {
  const { t } = useTranslation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isDisabled = !!disabledReason || isBusy;

  useEffect(() => {
    if (isBusy) setIsMenuOpen(false);
  }, [isBusy]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isMenuOpen]);

  return (
    <Tooltip
      title={
        disabledReason ? <TooltipText>{disabledReason}</TooltipText> : undefined
      }
      zIndex={3002}
      color="#4B4B4B"
      placement="top"
      arrow={false}
      styles={{
        body: {
          padding: "6px 12px",
          minHeight: "40px",
          width: "max-content",
          borderRadius: "4px !important",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Open Sans', sans-serif",
        },
      }}
    >
      <ResponseContainer ref={wrapperRef}>
        {isMenuOpen && (
          <PlusMenu role="menu">
            <PlusMenuItem
              type="button"
              role="menuitem"
              disabled={!isCustomAvailable}
              onClick={() => {
                setIsMenuOpen(false);
                onCustom();
              }}
            >
              <PlusMenuTitle disabled={!isCustomAvailable}>
                {t("patientPage_messages_thread_custom")}
              </PlusMenuTitle>
              <PlusMenuDescription>
                {isCustomAvailable
                  ? t("patientPage_messages_thread_customDescription")
                  : t("patientPage_messages_thread_customUnavailable")}
              </PlusMenuDescription>
            </PlusMenuItem>

            {/* "Add file" is hidden until file sharing ships (separate ticket). Restore this entry then.
            <PlusMenuItem type="button" role="menuitem">
              <PlusMenuTitle>Add file</PlusMenuTitle>
              <PlusMenuDescription>
                Share images or PDF documents
              </PlusMenuDescription>
            </PlusMenuItem>
            */}
          </PlusMenu>
        )}

        <PlusButton
          type="button"
          aria-label={t("patientPage_messages_thread_moreOptions")}
          disabled={isDisabled}
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          +
        </PlusButton>

        <TextArea
          placeholder={t("patientPage_messages_view_responsePlaceholder")}
          backgroudColor="white"
          className={`${TextAreaCss()} ${isClosed ? ReopenTextAreaCss() : ""}`}
          disabled={isDisabled}
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
        />

        <Button
          // Sending into a closed thread reopens it, so the control says so rather than
          // pretending nothing else happens.
          label={
            isClosed
              ? t("patientPage_messages_thread_sendAndReopen")
              : t("patientPage_messages_view_send")
          }
          type="button"
          size="small"
          disabled={isDisabled || !draft.trim()}
          isLoading={isSending}
          className={`${SendButtonCss()} ${
            isClosed ? SendAndReopenButtonCss() : ""
          }`}
          onClick={onSend}
        />
      </ResponseContainer>
    </Tooltip>
  );
}
